/**
 * Scene model — a DOM-free description of a widget's visual.
 *
 * A widget produces a tree of these nodes. The layout solver assigns
 * absolute rects; a backend (SVG today, HTML later) paints them. The
 * model carries flex *intent* (direction, gap, padding, grow, align,
 * justify) — NOT computed geometry — so it stays renderer-agnostic and
 * deterministic, and can be built + rendered with no browser.
 */

export interface FontSpec {
  /** CSS font-family list, used verbatim by the SVG backend. */
  readonly family: string;
  readonly size: number;
  readonly weight: number;
}

/** A linear gradient fill, expressed in CSS angle convention (0deg = to top, clockwise). */
export interface Gradient {
  readonly angle: number;
  readonly from: string;
  readonly to: string;
}

/** A drop shadow. `spread` is honored by HTML; SVG approximates (no spread). */
export interface Shadow {
  readonly dx: number;
  readonly dy: number;
  readonly blur: number;
  readonly spread?: number;
  readonly color: string;
}

export interface BoxStyle {
  /** Resolved fill color (hex/rgb), or undefined for transparent. */
  readonly fill?: string;
  /** Linear gradient fill; takes precedence over `fill` when set. */
  readonly gradient?: Gradient;
  readonly stroke?: string;
  readonly strokeWidth?: number;
  /** Corner radius in px — uniform, or per-corner [topLeft, topRight, bottomRight, bottomLeft]. */
  readonly radius?: number | readonly [number, number, number, number];
  /** Body outline shape. "ellipse" → an oval filling the box (overrides radius). */
  readonly shape?: "ellipse";
  readonly dashed?: boolean;
  /** 0..1; multiplies into the painted opacity. */
  readonly opacity?: number;
  /** Drop shadow under the box. */
  readonly shadow?: Shadow;
}

/** Where an overlay pins inside its parent box. */
export type Anchor =
  | "top-left" | "top-right" | "bottom-left" | "bottom-right"
  | "top" | "bottom" | "left" | "right";

/**
 * An absolutely-positioned child painted on top of the flow content —
 * for things flexbox can't express (a corner badge, a top accent
 * stripe, edge anchor dots). Positioned by `at`, never participates in
 * the flex solve.
 */
export interface Overlay {
  readonly node: Node;
  readonly at: Anchor;
  /** Gap from the anchored edges, px (default 0). */
  readonly inset?: number;
  /** Span the box's full width / height (e.g. a top stripe). */
  readonly stretchX?: boolean;
  readonly stretchY?: boolean;
}

export interface TextStyle {
  readonly font: FontSpec;
  readonly color: string;
  /** Horizontal placement within the allotted box. */
  readonly align?: "start" | "center" | "end";
  /** Single-line truncate-with-ellipsis when the text overflows width. */
  readonly ellipsis?: boolean;
  /** Wrap across multiple lines to fit the allotted width. */
  readonly wrap?: boolean;
  /** Cap wrapped lines; the last line is ellipsized if more remain. */
  readonly maxLines?: number;
  /** Font family for `code` runs (richtext). Defaults to a mono stack. */
  readonly monoFamily?: string;
  /** Strike the text through — the lifecycle "deprecated" treatment. Mirrors
   *  `TextRun.strike`, which only reaches richtext; plain titles need it too. */
  readonly strike?: boolean;
  /** Link color for `href` runs (richtext). Defaults to the accent. */
  readonly linkColor?: string;
  /** LOD: extra px added to font size at far zoom, tapering as
   *  `--arch-band-compact` rises (HTML) — a big name at dot zoom. */
  readonly bandShrink?: number;
}

/**
 * Continuous level-of-detail band. The board sets `--arch-band-{band}` and
 * `--arch-band-{band}-reveal` (0..1) on the widget host as the user zooms;
 * the HTML backend gates flex-grow / opacity on them so detail collapses +
 * reveals smoothly. The SVG backend (and standalone HTML) bakes full detail
 * (the var fallback is 1). "compact" ramps first, "full" deeper in.
 */
export type LodBand = "compact" | "full";

/** Cross-axis alignment of children within a box. */
export type Align = "start" | "center" | "end" | "stretch";
/** Main-axis distribution of children within a box. */
export type Justify = "start" | "center" | "end" | "between";

interface Common {
  /** Flex grow factor along the parent's main axis (default 0). */
  readonly grow?: number;
}

export interface BoxNode extends Common {
  readonly type: "box";
  readonly direction: "row" | "column";
  readonly gap?: number;
  /** Uniform px, or [top, right, bottom, left]. */
  readonly padding?: number | readonly [number, number, number, number];
  readonly align?: Align;
  readonly justify?: Justify;
  readonly style?: BoxStyle;
  readonly children: readonly Node[];
  /** Absolutely-positioned children painted over the flow content. */
  readonly overlays?: readonly Overlay[];
  /** LOD: gate this box's opacity on `--arch-band-{reveal}-reveal` (HTML). */
  readonly reveal?: LodBand;
  /** LOD: scale flex-grow by `--arch-band-{growBand}` with a 0 flex-basis,
   *  so the box collapses at far zoom and grows in (HTML). */
  readonly growBand?: LodBand;
  /** Fixed intrinsic size override (else derived from children/grow). */
  readonly width?: number;
  readonly height?: number;
}

export interface TextNode extends Common {
  readonly type: "text";
  readonly text: string;
  readonly style: TextStyle;
}

/** An inline icon that flows inside rich text like a leading "word". */
export interface InlineGlyph {
  readonly body: string;
  readonly viewW: number;
  readonly viewH: number;
  readonly color?: string;
  readonly width: number;
  readonly height: number;
}

/** One inline span of rich text — plain, bold, italic, strike, code, a link,
 *  or an inline glyph (`glyph` set; `text` then ignored for layout). */
export interface TextRun {
  readonly text: string;
  readonly bold?: boolean;
  readonly italic?: boolean;
  readonly strike?: boolean;
  /** Monospace inline code span. */
  readonly code?: boolean;
  /** Link target; renders as an anchor (HTML `<a>` / SVG `<a>`). */
  readonly href?: string;
  /** Dimmed/error sentinel (e.g. `<missing:…>`). */
  readonly dim?: boolean;
  /** Inline icon flowing in the text (e.g. the leading glyph before a name). */
  readonly glyph?: InlineGlyph;
}

/**
 * Multi-run text with inline bold/italic — the scene's answer to a
 * rendered-markdown body. `style` carries the base font/color/align and
 * (typically) `wrap` + `maxLines`; runs override weight/style per span.
 */
export interface RichTextNode extends Common {
  readonly type: "richtext";
  readonly runs: readonly TextRun[];
  readonly style: TextStyle;
}

export interface GlyphNode extends Common {
  readonly type: "glyph";
  /** Inner-SVG markup drawn against a `0 0 viewW viewH` viewBox. */
  readonly viewW: number;
  readonly viewH: number;
  readonly body: string;
  /** Resolves `currentColor` inside `body` when the SVG stands alone. */
  readonly color?: string;
  /** Intrinsic size when not stretched by the parent. */
  readonly width?: number;
  readonly height?: number;
  /** 0..1 — multiplies painted opacity. A glyph draws its own shape (the
   *  database cylinder, the device figures), so it needs its own fade: styling
   *  only the enclosing box would leave those shapes fully opaque. */
  readonly opacity?: number;
  /** Dash every stroke inside `body`. Same reason as `opacity`: a glyph-drawn
   *  shape has no enclosing stroked box to dash, so a PLANNED cylinder or
   *  device figure faded but stayed solid while every box-drawn shape dashed.
   *  Emitted as `stroke-dasharray` on the wrapping `<svg>`, which the body's
   *  own strokes inherit. */
  readonly dashed?: boolean;
}

export interface SpacerNode extends Common {
  readonly type: "spacer";
}

/**
 * A raster/vector image referenced by URL — a block-level markdown image
 * (`![alt](src)`). The HTML backend emits `<img>`, the SVG backend an
 * `<image>`; both fit the source into the node's box (`xMidYMid meet`) so the
 * two stay in lockstep. `src` is assumed already safety-checked by the
 * producer; `alt` backs the title/fallback. Width/height are the display box —
 * intrinsic dimensions of the source are not known headlessly, so the producer
 * supplies them (an explicit size hint or a default).
 */
export interface ImageNode extends Common {
  readonly type: "image";
  readonly src: string;
  readonly alt: string;
  readonly width: number;
  readonly height: number;
}

/**
 * A reserved hole the *host* fills — the scene's portal. Paints nothing
 * itself; the HTML backend emits a `part`-tagged, slot-bearing element at
 * the node's rect so the board can measure it (`[part="subspace"]`) and
 * project the nested space over it. The SVG backend renders it empty
 * (headless export can't host a live nested diagram).
 */
export interface SlotNode extends Common {
  readonly type: "slot";
  /** `part` attribute on the emitted host element (e.g. "subspace"). */
  readonly part?: string;
  /** Named `<slot>` for projected light-DOM fallback content. */
  readonly name?: string;
  readonly width?: number;
  readonly height?: number;
}

export type Node = BoxNode | TextNode | RichTextNode | GlyphNode | SpacerNode | SlotNode | ImageNode;

// ---- constructors (terse, validated-by-types) ------------------------------

export const box = (b: Omit<BoxNode, "type">): BoxNode => ({ type: "box", ...b });
export const text = (t: string, style: TextStyle, grow?: number): TextNode => ({
  type: "text",
  text: t,
  style,
  grow,
});
export const glyph = (g: Omit<GlyphNode, "type">): GlyphNode => ({ type: "glyph", ...g });
export const spacer = (grow = 1): SpacerNode => ({ type: "spacer", grow });
export const image = (img: Omit<ImageNode, "type">): ImageNode => ({ type: "image", ...img });
export const slot = (s: Omit<SlotNode, "type"> = {}): SlotNode => ({ type: "slot", ...s });
export const richtext = (runs: readonly TextRun[], style: TextStyle, grow?: number): RichTextNode => ({
  type: "richtext",
  runs,
  style,
  grow,
});

export function padOf(
  p: BoxNode["padding"],
): readonly [number, number, number, number] {
  if (p == null) return [0, 0, 0, 0];
  if (typeof p === "number") return [p, p, p, p];
  return p;
}
